import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import api from "@/api/client";
import useWebSocket from "@/hooks/useWebSocket";
import TopBar from "@/components/TopBar";
import BackButton from "@/components/BackButton";
import { Bike, MapPin, Store, RefreshCw, Users, ChevronLeft, Radio } from "lucide-react";

// ---------- Leaflet icon fixes + custom pins ----------
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const pin = (color, emoji, size = 36, badge = null) =>
  L.divIcon({
    className: "talabya-livemap-pin",
    html: `<div style="position:relative;background:${color};width:${size}px;height:${size}px;border-radius:50%;border:3px solid #fff;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;box-shadow:0 4px 14px rgba(0,0,0,0.28);font-size:${size / 2.2}px;">${emoji}${badge ? `<div style="position:absolute;top:-4px;right:-4px;width:18px;height:18px;background:#16a34a;border:2px solid #fff;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:10px;animation:talabya-call-pulse 1.4s infinite;">📞</div>` : ""}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });

const DRIVER_AVAILABLE = pin("#16a34a", "🏍");
const DRIVER_BUSY = pin("#eab308", "🏍");
const DRIVER_OFFLINE = pin("#6b7280", "🏍", 32);
const DRIVER_AVAILABLE_CALL = pin("#16a34a", "🏍", 36, true);
const DRIVER_BUSY_CALL = pin("#eab308", "🏍", 36, true);
const CUSTOMER_ICON = pin("#E05A3D", "📍");
const RESTAURANT_ICON = pin("#7c3aed", "🍽");

const STATUS_LABEL = {
  available: "متاح",
  busy: "مشغول",
  offline: "غير متصل",
  pending: "بانتظار المطعم",
  preparing: "قيد التحضير",
  ready: "جاهز للاستلام",
  assigned: "سائق قادم للاستلام",
  picked_up: "في الطريق للزبون",
};

function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `منذ ${Math.floor(diff)} ث`;
    if (diff < 3600) return `منذ ${Math.floor(diff / 60)} د`;
    if (diff < 86400) return `منذ ${Math.floor(diff / 3600)} س`;
    return d.toLocaleString("ar");
  } catch {
    return "—";
  }
}

export default function LiveMap() {
  const [data, setData] = useState({ drivers: [], customers: [], restaurants: [], generated_at: null });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ drivers: true, customers: true, restaurants: true });
  const [selected, setSelected] = useState(null); // {type, payload}
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({}); // id → L.Marker
  const [wsUpdatesCount, setWsUpdatesCount] = useState(0);
  // Live "in-call" tracking: driver_id -> { order_id, order_code, since }
  const [inCall, setInCall] = useState({});

  // ---------- Initial map ----------
  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    const map = L.map(mapEl.current, {
      center: [32.0665, 12.5303], // Yafran default
      zoom: 12,
      zoomControl: true,
      attributionControl: false,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // ---------- Load data ----------
  const load = async () => {
    try {
      const { data: d } = await api.get("/admin/live-map");
      setData(d);
    } catch (e) {
      console.warn("live-map load failed", e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  // ---------- WebSocket for real-time driver location updates ----------
  const { status: wsStatus } = useWebSocket({
    onMessage: (msg) => {
      if (msg.topic === "admin:live-map" && msg.event === "driver.location") {
        const p = msg.data || {};
        setWsUpdatesCount((n) => n + 1);
        setData((prev) => {
          const drivers = [...(prev.drivers || [])];
          const idx = drivers.findIndex((d) => d.user_id === p.driver_id);
          const patch = {
            user_id: p.driver_id,
            name: p.name,
            phone: p.phone,
            status: p.status || "available",
            location: p.location,
            updated_at: p.updated_at,
            stale: false,
          };
          if (idx >= 0) drivers[idx] = { ...drivers[idx], ...patch };
          else drivers.push(patch);
          return { ...prev, drivers };
        });
      } else if (msg.topic === "admin:live-map" && msg.event === "driver.offline") {
        // Driver just toggled to غير متصل — remove his marker instantly.
        const p = msg.data || {};
        if (p.driver_id) {
          setData((prev) => ({
            ...prev,
            drivers: (prev.drivers || []).filter((d) => d.user_id !== p.driver_id),
          }));
        }
      } else if (msg.topic === "admin:live-map" && msg.event === "driver.call.started") {
        const d = msg.data || {};
        if (d.driver_id) {
          setInCall((prev) => ({
            ...prev,
            [d.driver_id]: {
              order_id: d.order_id,
              order_code: d.order_code,
              customer_id: d.customer_id,
              since: d.at || new Date().toISOString(),
            },
          }));
        }
      } else if (msg.topic === "admin:live-map" && msg.event === "driver.call.ended") {
        const d = msg.data || {};
        if (d.driver_id) {
          setInCall((prev) => {
            const next = { ...prev };
            delete next[d.driver_id];
            return next;
          });
        }
      }
    },
  });

  // ---------- Render markers ----------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const keep = new Set();

    const setMarker = (key, latlng, icon, popup, onClick) => {
      keep.add(key);
      if (markersRef.current[key]) {
        markersRef.current[key].setLatLng(latlng);
        markersRef.current[key].setIcon(icon);
        markersRef.current[key].getPopup()?.setContent(popup);
      } else {
        const m = L.marker(latlng, { icon }).addTo(map).bindPopup(popup);
        m.on("click", () => onClick && onClick());
        markersRef.current[key] = m;
      }
    };

    if (filters.drivers) {
      (data.drivers || []).forEach((d) => {
        const key = `drv_${d.user_id}`;
        const isInCall = !!inCall[d.user_id];
        const icon =
          isInCall && d.status === "available" ? DRIVER_AVAILABLE_CALL :
          isInCall && d.status === "busy" ? DRIVER_BUSY_CALL :
          d.status === "available" ? DRIVER_AVAILABLE :
          d.status === "busy" ? DRIVER_BUSY : DRIVER_OFFLINE;
        const callInfo = inCall[d.user_id];
        const popup = `<div dir="rtl" style="font-family:Cairo;min-width:170px">
          <div style="font-weight:700;color:#E05A3D">🏍 ${d.name || "سائق"}</div>
          <div style="font-size:12px;color:#666">${STATUS_LABEL[d.status] || d.status}</div>
          ${d.phone ? `<div style="font-size:11px;direction:ltr;text-align:right">${d.phone}</div>` : ""}
          <div style="font-size:11px;color:#999">آخر تحديث: ${fmtRelative(d.updated_at)}</div>
          ${d.stale ? '<div style="color:#dc2626;font-size:11px;font-weight:700">⚠ قديم</div>' : ""}
          ${isInCall ? `<div style="margin-top:6px;padding:4px 8px;background:#dcfce7;border-radius:8px;font-size:11px;color:#166534;font-weight:700">📞 قيد الاتصال · طلب ${callInfo.order_code || callInfo.order_id?.slice(0,6) || ""}</div>` : ""}
        </div>`;
        setMarker(key, [d.location.lat, d.location.lng], icon, popup, () =>
          setSelected({ type: "driver", payload: { ...d, in_call: callInfo || null } })
        );
      });
    }
    if (filters.customers) {
      (data.customers || []).forEach((c) => {
        const key = `cus_${c.order_id}`;
        const popup = `<div dir="rtl" style="font-family:Cairo;min-width:180px">
          <div style="font-weight:700;color:#E05A3D">📍 ${c.customer_name}</div>
          <div style="font-size:12px;color:#666">${STATUS_LABEL[c.status] || c.status}</div>
          <div style="font-size:11px;color:#999">طلب #${c.order_code}</div>
          ${c.customer_phone ? `<div style="font-size:11px;direction:ltr;text-align:right">${c.customer_phone}</div>` : ""}
          ${c.address_notes ? `<div style="font-size:11px;color:#555;margin-top:4px">${c.address_notes}</div>` : ""}
        </div>`;
        setMarker(key, [c.location.lat, c.location.lng], CUSTOMER_ICON, popup, () =>
          setSelected({ type: "customer", payload: c })
        );
      });
    }
    if (filters.restaurants) {
      (data.restaurants || []).forEach((r) => {
        const key = `rst_${r.id}`;
        const popup = `<div dir="rtl" style="font-family:Cairo;min-width:180px">
          <div style="font-weight:700;color:#7c3aed">🍽 ${r.name}</div>
          <div style="font-size:12px;color:#666">${r.address || ""}</div>
          ${r.phone ? `<div style="font-size:11px;direction:ltr;text-align:right">${r.phone}</div>` : ""}
        </div>`;
        setMarker(key, [r.location.lat, r.location.lng], RESTAURANT_ICON, popup, () =>
          setSelected({ type: "restaurant", payload: r })
        );
      });
    }

    // Remove markers whose key isn't in the kept set
    Object.keys(markersRef.current).forEach((k) => {
      if (!keep.has(k)) {
        map.removeLayer(markersRef.current[k]);
        delete markersRef.current[k];
      }
    });
  }, [data, filters, inCall]);

  // ---------- Fit-bounds only on first load ----------
  const firstFitRef = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || firstFitRef.current) return;
    const pts = [];
    (data.drivers || []).forEach((d) => pts.push([d.location.lat, d.location.lng]));
    (data.customers || []).forEach((c) => pts.push([c.location.lat, c.location.lng]));
    (data.restaurants || []).forEach((r) => pts.push([r.location.lat, r.location.lng]));
    if (pts.length > 0) {
      const b = L.latLngBounds(pts);
      map.fitBounds(b, { padding: [40, 40], maxZoom: 14 });
      firstFitRef.current = true;
    }
  }, [data]);

  const stats = useMemo(() => {
    const drivers = data.drivers || [];
    return {
      available: drivers.filter((d) => d.status === "available" && !d.stale).length,
      busy: drivers.filter((d) => d.status === "busy" && !d.stale).length,
      offline: drivers.filter((d) => d.status === "offline" || d.stale).length,
      inCall: Object.keys(inCall).length,
      totalDrivers: drivers.length,
      activeOrders: (data.customers || []).length,
      restaurants: (data.restaurants || []).length,
    };
  }, [data, inCall]);

  const focusEntity = (lat, lng) => {
    const map = mapRef.current;
    if (map) map.flyTo([lat, lng], 15, { duration: 0.7 });
  };

  return (
    <div className="min-h-screen bg-[var(--surface-alt)]">
      <TopBar />
      <div className="max-w-7xl mx-auto px-4 pt-4 pb-2 flex items-center gap-2">
        <BackButton to="/admin" variant="pill" />
        <div className="flex-1">
          <h1 className="text-2xl font-black" style={{ fontFamily: "Cairo" }} data-testid="livemap-title">
            الخريطة الحية
          </h1>
          <p className="text-xs text-[var(--text-secondary)]">
            متابعة السائقين والزبائن والمطاعم على الخريطة في الوقت الفعلي
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="px-3 py-2 rounded-xl bg-white border border-[var(--border)] hover:bg-blue-50 text-sm font-bold flex items-center gap-1 disabled:opacity-60"
          data-testid="livemap-refresh"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> تحديث
        </button>
      </div>

      {/* Stats strip */}
      <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
        <StatChip color="#16a34a" icon={<Bike size={14} />} label="متاحون" value={stats.available} testid="stat-available" />
        <StatChip color="#eab308" icon={<Bike size={14} />} label="مشغولون" value={stats.busy} testid="stat-busy" />
        <StatChip color="#0891b2" icon={<Radio size={14} />} label="قيد الاتصال" value={stats.inCall} testid="stat-incall" />
        <StatChip color="#E05A3D" icon={<MapPin size={14} />} label="طلبات نشطة" value={stats.activeOrders} testid="stat-orders" />
        <StatChip color="#7c3aed" icon={<Store size={14} />} label="مطاعم" value={stats.restaurants} testid="stat-restaurants" />
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* Map */}
        <div className="relative rounded-3xl overflow-hidden bg-white talabya-shadow-soft" style={{ minHeight: "70vh" }}>
          <div ref={mapEl} style={{ width: "100%", height: "70vh" }} data-testid="livemap-canvas" />
          {/* Legend + filters */}
          <div className="absolute top-3 right-3 bg-white rounded-2xl shadow-lg p-3 space-y-1.5 z-[400]" data-testid="livemap-legend">
            <LegendToggle
              testid="filter-drivers"
              checked={filters.drivers}
              onChange={(v) => setFilters({ ...filters, drivers: v })}
              color="#16a34a"
              label={`السائقون (${stats.totalDrivers})`}
            />
            <LegendToggle
              testid="filter-customers"
              checked={filters.customers}
              onChange={(v) => setFilters({ ...filters, customers: v })}
              color="#E05A3D"
              label={`الزبائن (${stats.activeOrders})`}
            />
            <LegendToggle
              testid="filter-restaurants"
              checked={filters.restaurants}
              onChange={(v) => setFilters({ ...filters, restaurants: v })}
              color="#7c3aed"
              label={`المطاعم (${stats.restaurants})`}
            />
            <div className="pt-1 border-t border-gray-100 text-[10px] text-gray-500 flex items-center gap-1">
              <Radio size={10} className={wsStatus === "open" ? "text-green-500" : "text-gray-400"} />
              {wsStatus === "open" ? `تحديث حي (${wsUpdatesCount})` : "لا يوجد بث حي"}
            </div>
          </div>
        </div>

        {/* Side panel */}
        <div className="space-y-3">
          {selected ? (
            <SelectedPanel selected={selected} onClose={() => setSelected(null)} onFocus={focusEntity} />
          ) : (
            <div className="bg-white rounded-2xl talabya-shadow-soft p-4 text-sm text-[var(--text-secondary)]" data-testid="livemap-hint">
              اضغط على أي علامة على الخريطة لعرض تفاصيلها هنا. أو اختر عنصراً من القوائم أدناه.
            </div>
          )}

          <ListSection
            title="السائقون"
            icon={<Bike size={16} className="text-green-600" />}
            testid="drivers-list"
            items={(data.drivers || []).sort((a, b) => (a.stale === b.stale ? 0 : a.stale ? 1 : -1))}
            renderItem={(d) => (
              <button
                key={d.user_id}
                onClick={() => {
                  setSelected({ type: "driver", payload: d });
                  focusEntity(d.location.lat, d.location.lng);
                }}
                className={`w-full text-right px-3 py-2 rounded-lg hover:bg-blue-50 flex items-center gap-2 ${d.stale ? "opacity-50" : ""}`}
                data-testid={`drv-row-${d.user_id}`}
              >
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full"
                  style={{ background: d.status === "available" ? "#16a34a" : d.status === "busy" ? "#eab308" : "#6b7280" }}
                />
                <span className="flex-1 truncate font-semibold text-sm">{d.name}</span>
                <span className="text-[10px] text-gray-500">{fmtRelative(d.updated_at)}</span>
                <ChevronLeft size={14} className="text-gray-400" />
              </button>
            )}
          />

          <ListSection
            title="طلبات نشطة"
            icon={<Users size={16} className="text-primary-brand" />}
            testid="customers-list"
            items={data.customers || []}
            renderItem={(c) => (
              <button
                key={c.order_id}
                onClick={() => {
                  setSelected({ type: "customer", payload: c });
                  focusEntity(c.location.lat, c.location.lng);
                }}
                className="w-full text-right px-3 py-2 rounded-lg hover:bg-blue-50 flex items-center gap-2"
                data-testid={`cus-row-${c.order_id}`}
              >
                <MapPin size={12} className="text-primary-brand" />
                <span className="flex-1 truncate font-semibold text-sm">{c.customer_name}</span>
                <span className="text-[10px] text-gray-500">#{c.order_code}</span>
                <ChevronLeft size={14} className="text-gray-400" />
              </button>
            )}
          />
        </div>
      </div>
    </div>
  );
}

function StatChip({ color, icon, label, value, testid }) {
  return (
    <div
      className="bg-white rounded-2xl p-3 talabya-shadow-soft flex items-center gap-2"
      data-testid={testid}
    >
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0"
        style={{ background: color }}
      >
        {icon}
      </div>
      <div>
        <div className="text-[10px] text-[var(--text-secondary)] leading-tight">{label}</div>
        <div className="text-lg font-black" style={{ fontFamily: "Cairo" }}>
          {value}
        </div>
      </div>
    </div>
  );
}

function LegendToggle({ checked, onChange, color, label, testid }) {
  return (
    <label className="flex items-center gap-2 text-xs cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-primary-brand"
        data-testid={testid}
      />
      <span className="inline-block w-3 h-3 rounded-full" style={{ background: color }} />
      <span className="font-semibold">{label}</span>
    </label>
  );
}

function ListSection({ title, icon, items, renderItem, testid }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white rounded-2xl talabya-shadow-soft" data-testid={testid}>
      <button
        className="w-full flex items-center gap-2 px-4 py-3 border-b border-[var(--border)]"
        onClick={() => setOpen(!open)}
      >
        {icon}
        <span className="font-black text-sm flex-1 text-right" style={{ fontFamily: "Cairo" }}>
          {title} ({items.length})
        </span>
        <ChevronLeft size={14} className={`text-gray-400 transition-transform ${open ? "-rotate-90" : "rotate-0"}`} />
      </button>
      {open && (
        <div className="max-h-72 overflow-y-auto p-2 space-y-1">
          {items.length === 0 ? (
            <div className="text-xs text-gray-400 text-center py-4">لا يوجد بيانات</div>
          ) : (
            items.map((it) => renderItem(it))
          )}
        </div>
      )}
    </div>
  );
}

function SelectedPanel({ selected, onClose, onFocus }) {
  const { type, payload } = selected;
  return (
    <div className="bg-white rounded-2xl talabya-shadow-soft p-4 space-y-2" data-testid={`sel-panel-${type}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs px-2 py-1 rounded-lg bg-blue-100 text-primary-brand font-bold">
          {type === "driver" ? "سائق" : type === "customer" ? "طلب / زبون" : "مطعم"}
        </span>
        <button onClick={onClose} className="text-xs text-gray-500 hover:text-red-600" data-testid="sel-close">
          إغلاق ✕
        </button>
      </div>
      {type === "driver" && (
        <>
          <div className="text-lg font-black" style={{ fontFamily: "Cairo" }}>{payload.name}</div>
          <div className="text-xs text-gray-600">الحالة: {STATUS_LABEL[payload.status] || payload.status}</div>
          {payload.phone && <div className="text-xs" dir="ltr">{payload.phone}</div>}
          {payload.vehicle && <div className="text-xs">🏍 {payload.vehicle}</div>}
          {payload.rating != null && <div className="text-xs">⭐ {payload.rating}</div>}
          <div className="text-[11px] text-gray-500">آخر تحديث: {fmtRelative(payload.updated_at)}</div>
          {payload.stale && <div className="text-[11px] text-red-600 font-bold">⚠ الموقع قديم</div>}
          <button
            onClick={() => onFocus(payload.location.lat, payload.location.lng)}
            className="w-full mt-2 py-2 rounded-lg bg-primary-brand text-white text-sm font-bold"
            data-testid="sel-focus"
          >
            تركيز الخريطة
          </button>
        </>
      )}
      {type === "customer" && (
        <>
          <div className="text-lg font-black" style={{ fontFamily: "Cairo" }}>{payload.customer_name}</div>
          <div className="text-xs">طلب #{payload.order_code}</div>
          <div className="text-xs text-gray-600">{STATUS_LABEL[payload.status] || payload.status}</div>
          {payload.customer_phone && <div className="text-xs" dir="ltr">{payload.customer_phone}</div>}
          {payload.address_notes && <div className="text-xs text-gray-700">📍 {payload.address_notes}</div>}
          <div className="text-[11px] text-gray-500">تم الإنشاء: {fmtRelative(payload.created_at)}</div>
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => onFocus(payload.location.lat, payload.location.lng)}
              className="flex-1 py-2 rounded-lg bg-primary-brand text-white text-sm font-bold"
              data-testid="sel-focus"
            >
              تركيز
            </button>
            <Link
              to={`/track/${payload.order_id}`}
              className="flex-1 py-2 rounded-lg bg-gray-100 text-center text-sm font-bold"
              data-testid="sel-open-order"
            >
              فتح الطلب
            </Link>
          </div>
        </>
      )}
      {type === "restaurant" && (
        <>
          <div className="text-lg font-black" style={{ fontFamily: "Cairo" }}>{payload.name}</div>
          {payload.address && <div className="text-xs">{payload.address}</div>}
          {payload.phone && <div className="text-xs" dir="ltr">{payload.phone}</div>}
          <button
            onClick={() => onFocus(payload.location.lat, payload.location.lng)}
            className="w-full mt-2 py-2 rounded-lg bg-primary-brand text-white text-sm font-bold"
            data-testid="sel-focus"
          >
            تركيز الخريطة
          </button>
        </>
      )}
    </div>
  );
}
